import { useCallback, useMemo, useState } from "react";
import { Icon } from "@/components/ui/Icon";
import { PageHeader } from "@/components/ui/PageHeader";
import { useAuth } from "@/hooks/useAuth";
import { useSpace } from "@/hooks/useSpace";
import { adminScopeOf } from "@/domain/space";
import { useWorkdayConfig } from "@/hooks/useWorkdayConfig";
import { usePersonalEvents } from "@/hooks/usePersonalEvents";
import { useGoogleCalendar } from "@/hooks/useGoogleCalendar";
import { useUserCalendars } from "@/hooks/useUserCalendars";
import { useDailyShipmentsMonth } from "@/hooks/useDailyShipmentsMonth";
import { useAsconProdDays } from "@/hooks/useAsconProdDays";
import { kstToday } from "@/domain/rqc";
import { isWorkday, nonWorkdayReason } from "@/domain/workday";
import { CAL_WEEKDAYS, buildMonthCells, shiftMonth, type CalCell } from "@/domain/calendarGrid";
import { eventTimeLabel, type PersonalEvent } from "@/domain/personalEvent";
import { googleEventDays, googleTimeLabel, type GoogleEvent } from "@/domain/googleCalendar";
import { DEFAULT_CALENDAR_ID, VIS_TODO, calendarColorCss, visKeyGoogle, visKeyProd } from "@/domain/calendar";
import { longDateLabel, prodLineText, type ProdDay } from "@/domain/mySchedule";
import { PreviewNote } from "./PreviewNote";
import { LocalNote, TenantBadge, TodoRow } from "./ScheduleCards";
import { useEventEditor } from "./useEventEditor";
import { CalendarListCard } from "./CalendarListCard";

/**
 * 내 업무 › 일정·할 일 (/my/tasks) — B′ 미리보기 2차, 2026-10-19. 캘린더 묶음 개편 2026-10-29.
 *
 * 월 캘린더(6주 고정 격자 = domain/calendarGrid, 대시보드 캘린더 위젯과 같은 계산 · .cal-head/.cal-nav 머리 재사용)
 * + 고른 날짜의 목록 + 캘린더 목록(켜기/끄기·설정) + 할 일 체크리스트.
 * 칸 안(위에서 아래):
 *   - 날짜 · 휴일 이름
 *   - 일정 줄: 내 캘린더 일정·할 일·Google 일정을 **시각순으로 섞어** 왼쪽 색 띠(캘린더 색)로 보인다. 넘치면 "+N".
 *   - 맨 아래 생산 칩(출하 권한자만, 2026-10-29 사용자 요청으로 하단·법인 이름 없이 색만): 중앙 ㎥(파랑) · 경원 톤(초록).
 *     오늘 이후 = 예정(점선 테두리만, 글자 없음), 지난 날 = 실적(틴트). 예정을 실적으로 쓰지 않도록 모양을 나누고 title·날짜 카드 태그로 구분한다.
 * 켜고 끄기는 오른쪽 캘린더 목록 카드(서버 저장). 날짜 칸 오른쪽 위 [+] = 그 날짜로 새 일정. 칸을 누르면 목록이 그날로.
 */

/** 칸 한 줄로 그릴 항목(ERP·Google 공용) */
interface CellLine {
  key: string;
  title: string;
  color: string;
  todo: boolean;
  done: boolean;
  allDay: boolean;
  time: string;
}

interface DayMarks {
  joongang?: { kind: "plan" | "actual"; m3: number };
  gyeongwon?: ProdDay;
  items: PersonalEvent[];
  google: GoogleEvent[];
}

const num = (v: number) => Math.round(v).toLocaleString("ko-KR");
/** 칸 안 일정 최대 줄 수 (넘으면 "+N") */
const CELL_MAX_ITEMS = 3;

export function MySpaceTasks() {
  const { tenants, membership } = useSpace();
  const { user, hasPerm } = useAuth();
  const scope = adminScopeOf(membership, !!user?.isAdmin);
  const { config } = useWorkdayConfig();
  const today = kstToday();
  const [ty, tm] = today.split("-").map(Number);

  const [view, setView] = useState({ y: ty, m: tm });
  const [picked, setPicked] = useState(today);

  const cal = useUserCalendars();
  const shipPerm = hasPerm("shipment.view") || hasPerm("dispatch.view");
  const canJoongang = shipPerm && (tenants.includes("joongang") || scope.tenants.includes("joongang"));
  const canGyeongwon = shipPerm && (tenants.includes("gyeongwon") || scope.tenants.includes("gyeongwon"));
  const showJoongang = canJoongang && cal.isVisible(visKeyProd("joongang"));
  const showGyeongwon = canGyeongwon && cal.isVisible(visKeyProd("gyeongwon"));

  const { byDayDoc } = useDailyShipmentsMonth(view.y, view.m, showJoongang, "joongang"); // 내 업무 달력의 중앙 생산 겹쳐 보기 — 법인 명시
  // 경원 = 격자 6주 전체(앞뒤 달 날짜 포함) 수주
  const cells = useMemo<CalCell[]>(() => buildMonthCells(view.y, view.m), [view.y, view.m]);
  const { days: gyeongwon } = useAsconProdDays(showGyeongwon, cells[0]?.date ?? "", cells[cells.length - 1]?.date ?? "");
  const gMock = gyeongwon.some((d) => d.mock);

  const pe = usePersonalEvents();
  const gcal = useGoogleCalendar();
  const holidayLabel = useCallback((ymd: string) => nonWorkdayReason(ymd, config), [config]);
  const editor = useEventEditor(pe, picked, holidayLabel, gcal, cal);

  const monthPrefix = `${view.y}-${String(view.m).padStart(2, "0")}-`;

  // 켠 캘린더의 것만
  const visibleItems = useMemo(
    () =>
      pe.events.filter((ev) =>
        ev.kind === "todo" ? cal.isVisible(VIS_TODO) : cal.isVisible(ev.calendar_id ?? DEFAULT_CALENDAR_ID),
      ),
    [pe.events, cal],
  );
  const visibleGoogle = useMemo(() => gcal.events.filter((ev) => cal.isVisible(visKeyGoogle(ev.calendar_id))), [gcal.events, cal]);

  const marks = useMemo(() => {
    const map = new Map<string, DayMarks>();
    const get = (d: string) => {
      let x = map.get(d);
      if (!x) { x = { items: [], google: [] }; map.set(d, x); }
      return x;
    };
    for (const ev of visibleItems) get(ev.date).items.push(ev);
    for (const ev of visibleGoogle) for (const d of googleEventDays(ev)) get(d).google.push(ev);
    if (showGyeongwon) for (const g of gyeongwon) get(g.date).gyeongwon = g;
    if (showJoongang) {
      for (const [day, doc] of byDayDoc) {
        const date = `${monthPrefix}${String(day).padStart(2, "0")}`;
        if (date >= today) {
          const p = doc.total_planned_m3 ?? 0;
          if (p > 0) get(date).joongang = { kind: "plan", m3: p };
        } else if (doc.total_m3 > 0) {
          get(date).joongang = { kind: "actual", m3: doc.total_m3 };
        }
      }
    }
    return map;
  }, [visibleItems, visibleGoogle, gyeongwon, showJoongang, showGyeongwon, byDayDoc, monthPrefix, today]);

  /** 칸 줄 — ERP·Google 섞어 종일 먼저 → 시각 → 할 일은 뒤 */
  const cellLines = useCallback(
    (m: DayMarks | undefined): CellLine[] => {
      if (!m) return [];
      const out: CellLine[] = [];
      for (const ev of m.items) {
        out.push({
          key: ev.id, title: ev.title,
          color: calendarColorCss(cal.calendarOf(ev.calendar_id).color),
          todo: ev.kind === "todo", done: ev.done, allDay: ev.all_day, time: ev.start_time ?? "",
        });
      }
      for (const ev of m.google) {
        out.push({
          key: ev.id, title: ev.title,
          color: gcal.calendarOf(ev.calendar_id)?.color || "var(--c-info)",
          todo: false, done: false, allDay: ev.all_day, time: ev.start_time ?? "",
        });
      }
      return out.sort((a, b) => {
        if (a.todo !== b.todo) return a.todo ? 1 : -1;
        if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
        return a.time < b.time ? -1 : a.time > b.time ? 1 : 0;
      });
    },
    [cal, gcal],
  );

  const isCurMonth = view.y === ty && view.m === tm;
  const move = (delta: number) => setView((v) => shiftMonth(v, delta));
  const goToday = () => { setView({ y: ty, m: tm }); setPicked(today); };
  const pick = (c: CalCell) => {
    setPicked(c.date);
    if (c.outside !== 0) setView((v) => shiftMonth(v, c.outside));
  };

  const day = marks.get(picked);
  const dayEvents = (day?.items ?? []).filter((e) => e.kind === "event");
  const dayTodos = (day?.items ?? []).filter((e) => e.kind === "todo");
  const dayGoogle = day?.google ?? [];
  const dayOff = nonWorkdayReason(picked, config);

  const openTodos = pe.events.filter((e) => e.kind === "todo" && !e.done);
  const doneCount = pe.events.filter((e) => e.kind === "todo" && e.done).length;

  return (
    <div className="page my-space">
      <PreviewNote />
      <PageHeader
        title="일정·할 일"
        desc="내 캘린더와 연동 캘린더, 법인 생산 일정과 휴일을 한 달 단위로 봅니다. 내 캘린더의 일정·할 일은 나만 봅니다."
      />

      <div className="my-tasks">
        <section className="card my-cal">
          <div className="cal-head">
            <div className="cal-title-wrap">
              <strong className="cal-title my-cal-title">{view.y}년 {view.m}월</strong>
            </div>
            <div className="cal-nav-group">
              <button type="button" className="cal-nav" onClick={() => move(-1)} aria-label="이전 달">
                <span className="cal-nav-prev"><Icon name="chevR" size={14} /></span>
              </button>
              <button type="button" className="cal-nav cal-nav-today" onClick={goToday} disabled={isCurMonth && picked === today}>
                오늘
              </button>
              <button type="button" className="cal-nav" onClick={() => move(1)} aria-label="다음 달">
                <Icon name="chevR" size={14} />
              </button>
            </div>
          </div>
          {(showJoongang || showGyeongwon) && (
            <div className="my-cal-legend">
              {showJoongang && <span className="my-cal-lg"><i className="my-cal-pchip is-sample is-joongang is-actual" />중앙 ㎥</span>}
              {showGyeongwon && <span className="my-cal-lg"><i className="my-cal-pchip is-sample is-gyeongwon is-actual" />경원 톤</span>}
              <span className="my-cal-lg"><i className="my-cal-pchip is-sample is-plan" />점선 = 예정·수주</span>
              {gMock && <span className="my-cal-lg my-tag is-mock">경원 가상 자료</span>}
            </div>
          )}
          <div className="my-cal-grid my-cal-dow" aria-hidden="true">
            {CAL_WEEKDAYS.map((w, i) => (
              <div key={w} className={`my-cal-dow-cell${i === 0 ? " is-sun" : i === 6 ? " is-sat" : ""}`}>{w}</div>
            ))}
          </div>
          <div className="my-cal-grid my-cal-days">
            {cells.map((c, i) => {
              const m = marks.get(c.date);
              const off = !isWorkday(c.date, config);
              const offLbl = c.outside === 0 && off ? nonWorkdayReason(c.date, config) : null;
              const lines = cellLines(m);
              const shown = lines.slice(0, CELL_MAX_ITEMS);
              const more = lines.length - shown.length;
              const cls = [
                "my-cal-cell",
                c.outside !== 0 ? "is-outside" : "",
                i % 7 === 0 ? "is-sun" : i % 7 === 6 ? "is-sat" : "",
                off ? "is-off" : "",
                c.date === today ? "is-today" : "",
                c.date === picked ? "is-picked" : "",
              ].filter(Boolean).join(" ");
              return (
                <div key={c.date} className={cls}>
                  <button
                    type="button"
                    className="my-cal-hit"
                    aria-pressed={c.date === picked}
                    aria-label={`${c.date}${offLbl ? ` ${offLbl}` : ""}${lines.length ? ` 일정 ${lines.length}건` : ""}`}
                    onClick={() => pick(c)}
                  />
                  <div className="my-cal-top">
                    <span className="my-cal-num">{c.day}</span>
                    {offLbl && <span className="my-cal-off">{offLbl}</span>}
                  </div>
                  {shown.map((l) => (
                    <div
                      key={l.key}
                      className={`my-cal-line${l.todo ? " is-todo" : ""}${l.done ? " is-done" : ""}`}
                      style={{ "--line-c": l.color } as React.CSSProperties}
                    >
                      {!l.allDay && l.time && <span className="my-cal-line-t mono">{l.time}</span>}
                      {l.title}
                    </div>
                  ))}
                  {more > 0 && <div className="my-cal-more">+{more}</div>}
                  {(m?.joongang || m?.gyeongwon) && (
                    <div className="my-cal-prod">
                      {m.joongang && (
                        <span className={`my-cal-pchip is-joongang is-${m.joongang.kind}`} title={`중앙 ${m.joongang.kind === "plan" ? "예정" : "실적"}`}>
                          <span className="mono">{num(m.joongang.m3)}</span>㎥
                        </span>
                      )}
                      {m.gyeongwon && (
                        <span className={`my-cal-pchip is-gyeongwon is-${m.gyeongwon.kind === "actual" ? "actual" : "plan"}`} title={`경원 ${m.gyeongwon.kind === "actual" ? "실적" : "수주"}`}>
                          {m.gyeongwon.total > 0 ? <><span className="mono">{num(m.gyeongwon.total)}</span>톤</> : "수주"}
                        </span>
                      )}
                    </div>
                  )}
                  <button
                    type="button"
                    className="my-cal-add"
                    aria-label={`${c.date} 일정 추가`}
                    onClick={() => editor.openNew(c.date, "event")}
                  >
                    <Icon name="plus" size={12} />
                  </button>
                </div>
              );
            })}
          </div>
          <LocalNote mode={pe.mode} />
        </section>

        <div className="my-col">
          <section className="card">
            <div className="card-hd">
              <div>
                <div className="card-title">{picked === today ? "오늘" : longDateLabel(picked)}</div>
                <div className="card-sub">
                  {picked === today ? longDateLabel(picked) : picked}
                  {dayOff && <span className="badge badge-warn my-day-off">{dayOff}</span>}
                </div>
              </div>
              <div className="card-actions">
                <button type="button" className="btn-tiny" onClick={() => editor.openNew(picked, "event")}>
                  <Icon name="plus" size={14} />
                  일정
                </button>
                <button type="button" className="btn-tiny" onClick={() => editor.openNew(picked, "todo")}>
                  <Icon name="plus" size={14} />
                  할 일
                </button>
              </div>
            </div>
            <ul className="my-list">
              {showJoongang && (
                <li className="my-row">
                  <TenantBadge t="joongang" />
                  {day?.joongang ? (
                    <span className="my-row-title">
                      <span className="my-prod-total mono">총 {num(day.joongang.m3)}㎥</span>
                    </span>
                  ) : (
                    <span className="my-row-title my-muted">{picked >= today ? "ERP 예정 없음" : "출하 기록 없음"}</span>
                  )}
                  {day?.joongang && <span className="my-tag">{day.joongang.kind === "plan" ? "예정" : "실적"}</span>}
                </li>
              )}
              {showGyeongwon && (
                <li className="my-row">
                  <TenantBadge t="gyeongwon" />
                  {day?.gyeongwon ? (
                    <span className="my-row-title">
                      {(() => {
                        const t = prodLineText(day.gyeongwon);
                        return (
                          <>
                            {t.site && <span className="my-prod-site">{t.site}</span>}
                            {t.site && <span className="my-prod-dot" aria-hidden="true">·</span>}
                            <span className="my-prod-total mono">{t.total}</span>
                          </>
                        );
                      })()}
                    </span>
                  ) : (
                    <span className="my-row-title my-muted">{picked >= today ? "수주 없음" : "출하 기록 없음"}</span>
                  )}
                  {day?.gyeongwon && <span className="my-tag">{day.gyeongwon.kind === "actual" ? "실적" : "수주"}</span>}
                  {gMock && <span className="my-tag is-mock">가상</span>}
                </li>
              )}
              {dayEvents.map((ev) => {
                const c = cal.calendarOf(ev.calendar_id);
                return (
                  <li key={ev.id} className="my-row">
                    <button type="button" className="my-row-btn" onClick={() => editor.openEdit(ev)} aria-label={`${ev.title} 일정 수정`}>
                      <span className="my-tbadge sp-cal">
                        <i className="my-cl-dot" style={{ background: calendarColorCss(c.color) }} aria-hidden="true" />
                        {c.name}
                      </span>
                      <span className="my-row-time mono">{eventTimeLabel(ev)}</span>
                      <span className="my-row-title">{ev.title}</span>
                      <Icon name="pencil" size={14} />
                    </button>
                  </li>
                );
              })}
              {dayGoogle.map((ev) => (
                <li key={ev.id} className="my-row">
                  <button type="button" className="my-row-btn" onClick={() => editor.openGoogle(ev)} aria-label={`${ev.title} Google 일정 열기`}>
                    <span className="my-tbadge sp-google">
                      <i className="my-gcal-dot" style={{ background: gcal.calendarOf(ev.calendar_id)?.color || undefined }} aria-hidden="true" />
                      {gcal.calendarOf(ev.calendar_id)?.name ?? "Google"}
                    </span>
                    <span className="my-row-time mono">{googleTimeLabel(ev)}</span>
                    <span className="my-row-title">{ev.title}</span>
                    <Icon name={ev.can_edit ? "pencil" : "info"} size={14} />
                  </button>
                </li>
              ))}
              {dayTodos.map((ev) => (
                <TodoRow key={ev.id} ev={ev} today={today} onToggle={(x) => void editor.toggleDone(x)} onOpen={editor.openEdit} />
              ))}
            </ul>
            {dayEvents.length === 0 && dayTodos.length === 0 && dayGoogle.length === 0 && (
              <div className="my-block-empty">이 날 일정·할 일이 없습니다</div>
            )}
          </section>

          <CalendarListCard cal={cal} g={gcal} showJoongang={canJoongang} showGyeongwon={canGyeongwon} gyeongwonMock={gMock} />

          <section className="card">
            <div className="card-hd">
              <div>
                <div className="card-title">할 일</div>
                <div className="card-sub">남은 {openTodos.length}건{doneCount > 0 ? ` · 완료 ${doneCount}건` : ""}</div>
              </div>
            </div>
            {openTodos.length === 0 ? (
              <div className="my-block-empty">남은 할 일이 없습니다</div>
            ) : (
              <ul className="my-list">
                {openTodos.map((ev) => (
                  <TodoRow key={ev.id} ev={ev} today={today} onToggle={(x) => void editor.toggleDone(x)} onOpen={editor.openEdit} />
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>

      {editor.modal}
    </div>
  );
}
